import { Link, useParams } from 'react-router-dom'

import { useClock, useLearningTrackDetail } from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
import {
  LearningBand,
  PacePill,
  PendingButton,
  ProgressPill,
  Unavailable,
} from '../components/learning/Chrome'
import { Curriculum } from '../components/learning/Curriculum'
import { PaceChart } from '../components/learning/PaceChart'
import { SessionLog } from '../components/learning/SessionLog'
import { ChevronLeft, PlanDoc, Reschedule } from '../components/learning/icons'
import { importedOn, planWeeks } from '../components/learning/derive'

/**
 * ONE LEARNING TRACK — docs/design/handoff/"LifeOS Learning.dc.html", option 9b.
 *
 * Plan provenance at the top, the curriculum outline with a current-position marker down the left,
 * the pace chart and the session log down the right.
 *
 * PROVENANCE IS THE FIRST THING ON THE PAGE, above the curriculum, because it is the sentence that
 * makes the rest of the screen readable: this plan was IMPORTED, on a stated day, from a stated
 * source, and nothing in LifeOS wrote it. The design's own headline for this screen says so — "the
 * plan is imported, never authored here" — and a curriculum with no visible author is the exact
 * shape of a system quietly claiming to know what you should study.
 *
 * PROGRESS IS THE COUNT PILL, never a ring (docs/design/FIDELITY.md, ratified deviation):
 * `25% · 46 of 182`, teal, in the band beside the title.
 */
export function LearningTrack() {
  const { trackId } = useParams<{ trackId: string }>()
  const { data: clock } = useClock()
  const { data: detail, isPending, error } = useLearningTrackDetail(trackId)

  const today = clock?.date
  const track = detail?.track

  return (
    <>
      <LearningBand>
        <div className="flex flex-none items-center gap-4 whitespace-nowrap">
          <Link
            to="/learning"
            className="flex items-center gap-2 text-[12px] font-medium leading-none text-muted hover:text-ink"
          >
            <ChevronLeft size={15} />
            Learning
          </Link>
          <span className="h-[14px] w-px bg-dash" aria-hidden />
          <span className="text-screen font-semibold">
            {track ? `${track.name} · ${track.subtitle}` : 'Track'}
          </span>
        </div>

        {detail && track && (
          <div className="flex flex-none items-center gap-[7px]">
            <ProgressPill
              percent={detail.percent}
              done={track.itemsDone}
              total={track.itemsTotal}
            />
            <PacePill pace={detail.pace} />
          </div>
        )}

        {/* The two actions belong to a track. On a page that has none — a bad id, or a read that
            failed — they are absent rather than disabled: a control for a thing that is not there
            is not a control. */}
        <div className="ml-auto flex flex-none items-center gap-[9px]">
          {track && (
            <>
              <PendingButton
                icon={<Reschedule size={13} className="text-muted" />}
                title="Re-flowing the remaining items across new dates arrives with V3 (the track-ingestion API). Life happens and the plan should move — but a reschedule that only redrew this screen would be a lie the next time the plan is read back."
              >
                Reschedule remaining
              </PendingButton>
              <PendingButton
                variant="dark"
                title="Starting a timed session arrives with V3. Today’s item can already be checked off in place, on this page and on the overview."
              >
                Start today’s item
              </PendingButton>
            </>
          )}
          <DataModeBadge />
        </div>
      </LearningBand>

      <div className="flex min-h-0 flex-1 gap-[14px] overflow-hidden px-[30px] pb-[26px]">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          {isPending && (
            <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
              <SkeletonRows rows={6} />
            </div>
          )}
          <Unavailable error={error} />

          {/* A track id that matches nothing is a real, sayable answer — not a blank page and not
              an error. It says what happened and offers the one way back. */}
          {detail === null && (
            <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
              <p className="max-w-[620px] text-body text-ink-3 text-pretty">
                There is no track called “{trackId}”. It may have been removed, or the link may be
                from an older plan.{' '}
                <Link to="/learning" className="font-medium text-teal underline underline-offset-2">
                  Back to Learning
                </Link>
                .
              </p>
            </div>
          )}

          {detail && track && (
            <>
              <div className="flex flex-none items-center gap-[11px] rounded-card border border-line bg-surface px-4 py-[11px]">
                <PlanDoc size={14} className="flex-none text-muted" />
                <span className="text-label font-medium text-ink-2">Plan {track.planVersion}</span>
                <Dot />
                <span className="text-label font-normal text-muted">
                  imported {importedOn(track.importedAt)}
                </span>
                <Dot />
                <span className="text-label font-normal text-muted">
                  {planWeeks(track.planStartDate, track.planEndDate)} weeks
                </span>
                <Dot />
                <span className="text-label font-normal text-muted">
                  source: {track.planSource}
                </span>
                <button
                  type="button"
                  disabled
                  title="The imported plan file itself is kept once plans live in the database (V3). This dataset holds the curriculum but not the document it came from, so there is nothing to open yet."
                  className="ml-auto cursor-not-allowed text-micro font-semibold text-muted"
                >
                  View plan
                </button>
              </div>

              {today && <Curriculum detail={detail} today={today} />}
            </>
          )}
        </div>

        {detail && track && today && (
          <div className="flex min-h-0 w-[454px] flex-none flex-col gap-3">
            <PaceChart
              series={detail.paceSeries}
              pace={detail.pace}
              total={track.itemsTotal}
              planStart={track.planStartDate}
              planEnd={track.planEndDate}
              today={today}
            />
            <SessionLog sessions={detail.sessions} totalMinutes={detail.totalMinutes} />
          </div>
        )}
      </div>
    </>
  )
}

const Dot = () => <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
